import { Box, Theme, Typography } from "@outschool/backpack";
import { TrendingSearchTopic } from "@outschool/gql-backend-generated";
import {
  BASE_LOCALE,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { startCaseProperNoun } from "@outschool/text";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { Container } from "@outschool/ui-legacy-component-library";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import SubjectIcon from "../SubjectIcon";
import OnlineClassesPageLink from "../OnlineClassesPageLink";
import useActivityString from "../translations/useActivityString";

export const subjectsToHighlight: string[] = [
  "Arts",
  "Coding & Tech",
  "English",
  "Life Skills",
  "Math",
  "Music",
  "Science & Nature",
  "Social Studies",
  "World Languages",
  "Learner Favorites",
];

const BrowseGridQuery = gql`
  query BrowseGrid($subject: String) {
    trendingSearchTopics(subject: $subject) {
      topic
      translatedTopic
    }
  }
`;

export function useSubjectKeywordLinksData() {
  // Call hooks individually for each subject
  const artsData = useTrendingSearchTopics("Arts");
  const codingData = useTrendingSearchTopics("Coding & Tech");
  const englishData = useTrendingSearchTopics("English");
  const lifeSkillsData = useTrendingSearchTopics("Life Skills");
  const mathData = useTrendingSearchTopics("Math");
  const musicData = useTrendingSearchTopics("Music");
  const scienceData = useTrendingSearchTopics("Science & Nature");
  const socialStudiesData = useTrendingSearchTopics("Social Studies");
  const worldLanguagesData = useTrendingSearchTopics("World Languages");
  const learnerFavoritesData = useTrendingSearchTopics("Learner Favorites");

  return {
    Arts: artsData,
    "Coding & Tech": codingData,
    English: englishData,
    "Life Skills": lifeSkillsData,
    Math: mathData,
    Music: musicData,
    "Science & Nature": scienceData,
    "Social Studies": socialStudiesData,
    "World Languages": worldLanguagesData,
    "Learner Favorites": learnerFavoritesData,
  };
}

function useTrendingSearchTopics(subject: string) {
  const { data: { trendingSearchTopics = [] } = {} } = useQueryWithPreviousData(
    BrowseGridQuery,
    {
      variables: { subject },
      context: { doNotRetry: true },
    },
  );

  return trendingSearchTopics;
}

function SubjectKeywordLinks({
  subject,
  maxKeywords = 10,
  data,
}: {
  subject: string;
  maxKeywords?: number;
  data?: any;
}) {
  const { t } = useTranslation("ui-components-website\\HomePage\\BrowseGrid");

  const trendingTopics = data.slice(0, maxKeywords);
  const trackingName = "search_link";
  const { translateThemeName } = useActivityString();
  const subjectString = translateThemeName(subject);
  const locale = useLocale();

  return (
    <Box>
      <Typography variant="h3">
        <SubjectIcon subjectName={subject} /> {subjectString}
      </Typography>
      <Box
        flex
        sx={{
          flexWrap: "wrap",
        }}
      >
        {trendingTopics.map((item: TrendingSearchTopic) => {
          const topic =
            locale === BASE_LOCALE
              ? startCaseProperNoun(item.topic)
              : item.translatedTopic;
          return (
            <Box
              key={item.topic}
              sx={{
                padding: "5px",
              }}
            >
              <OnlineClassesPageLink
                pageName={item.topic}
                trackingName={trackingName}
              >
                {topic}
              </OnlineClassesPageLink>
              <Typography
                variant="inherit"
                sx={{
                  paddingX: "0.5em",
                  color: "grey.100",
                }}
              >
                |
              </Typography>
            </Box>
          );
        })}
        <Box
          key={subject + "All"}
          sx={{
            padding: "5px",
          }}
        >
          {subject === "Learner Favorites" ? (
            <OnlineClassesPageLink
              // eslint-disable-next-line i18next/no-literal-string
              pageName={"popular"}
              trackingName={trackingName}
            >
              {t("All Learner Favorites")}
            </OnlineClassesPageLink>
          ) : (
            <OnlineClassesPageLink
              pageName={subject}
              trackingName={trackingName}
            >
              {t("All {{subjectString}} Classes", { subjectString })}
            </OnlineClassesPageLink>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function BrowseGridBlock({
  color,
  backgroundColor,
  headingText,
  linkTo,
  trackingId,
}: {
  color: string;
  backgroundColor: string;
  headingText: string;
  linkTo: string;
  trackingId: string;
}) {
  const { t } = useTranslation("ui-components-website\\HomePage\\BrowseGrid");

  const Morning = "&startAfterTime=6&endByTime=12";
  const Afternoon = "&startAfterTime=12&endByTime=17";
  const Evening = "&startAfterTime=16&endByTime=22";

  const border = "1px solid rgba(23, 25, 28, 0.1)";
  const Link = useLinkComponent();

  return (
    <Box
      sx={{
        backgroundColor,
        color,
        borderRadius: "0.5em",
        textAlign: "center",
        border,
      }}
    >
      <Link to={linkTo}>
        <Typography
          variant="h3"
          sx={{
            paddingY: "1em",
            color,
          }}
        >
          {headingText}
        </Typography>
      </Link>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          borderTop: border,
          justifyContent: "space-between",
        }}
      >
        <Link to={linkTo + Morning}>
          <Box
            sx={{
              paddingY: "0.5em",
              paddingX: "1em",
              borderRight: border,
              color,
            }}
          >
            {t("Morning")}
          </Box>
        </Link>
        <Link
          to={linkTo + Afternoon}
          trackingUniqueId={trackingId + "_afternoon"}
        >
          <Box
            sx={{
              paddingY: "0.5em",
              paddingX: "1em",
              color,
            }}
          >
            {t("Afternoon")}
          </Box>
        </Link>
        <Link to={linkTo + Evening} trackingUniqueId={trackingId + "_evening"}>
          <Box
            sx={{
              paddingY: "0.5em",
              paddingX: "1em",
              borderLeft: border,
              color,
            }}
          >
            {t("Evening")}
          </Box>
        </Link>
      </Box>
    </Box>
  );
}

function BrowseGrid(
  {
    subjectKeywordLinksData,
    title,
    showTopBoxes = true,
  }: {
    subjectKeywordLinksData: any;
    title?: string;
    showTopBoxes?: boolean;
  },
  ref: React.RefObject<HTMLDivElement>,
) {
  const { t } = useTranslation("ui-components-website\\HomePage\\BrowseGrid");

  const dayjsToday = () => dayjs().startOf("day");
  const nextWeek =
    "startAfter=" +
    dayjsToday().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD");
  const nextMonth =
    "startAfter=" +
    dayjsToday().add(1, "month").startOf("M").format("YYYY-MM-DD");

  const semesterCourses = "delivery=Semester%20course";

  const link = "/search?";

  return (
    <Container>
      <Box ref={ref}>
        {title && (
          <Typography
            variant="h2"
            sx={(theme: Theme) => ({
              textAlign: "center",
              marginBottom: "1em",

              [theme.breakpoints.up("md")]: {
                marginBottom: "2em",
              },
            })}
          >
            {title}
          </Typography>
        )}
        {showTopBoxes && (
          <Box
            sx={(theme: Theme) => ({
              display: "grid",
              gap: "1em",
              gridTemplateColumns: "1fr",

              [theme.breakpoints.up("lg")]: {
                gridTemplateColumns: "1fr 1fr 1fr",
              },

              marginBottom: "2em",
            })}
          >
            <BrowseGridBlock
              backgroundColor="#F6DDFB"
              color="#7B4BAE"
              headingText={t("Next Week")}
              linkTo={link + nextWeek}
              trackingId="browse_next_week"
            />
            <BrowseGridBlock
              backgroundColor="#FFE8C6"
              color="#A94D26"
              headingText={t("Next Month")}
              linkTo={link + nextMonth}
              trackingId="browse_next_month"
            />
            <BrowseGridBlock
              backgroundColor="#EDFDB6"
              color="#487537"
              headingText={t("Semester Courses")}
              linkTo={link + semesterCourses}
              trackingId="browse_custom_Semester courses"
            />
          </Box>
        )}
        <Box
          sx={(theme: Theme) => ({
            display: "grid",
            gridTemplateColumns: "1fr",

            [theme.breakpoints.up("md")]: {
              gridTemplateColumns: "1fr 1fr",
            },

            gap: "1em",
            marginBottom: "2em",
          })}
        >
          {subjectKeywordLinksData &&
            subjectsToHighlight.map(item => (
              <SubjectKeywordLinks
                key={item}
                subject={item}
                data={subjectKeywordLinksData[item]}
              />
            ))}
        </Box>
      </Box>
    </Container>
  );
}

export default React.forwardRef(BrowseGrid);
